import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/atoms/modal'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Button } from '@/components/atoms'
import { ApprovedStatusType } from '@/models/reservation-approved-status'
import { ButtonType } from '@/components/atoms/button'
import { Select } from '@/components/atoms/select'
import { Select as AntSelect } from 'antd'
import { Checkbox } from '@/components/atoms/checkbox'
import { LoaderContextCreator } from '@/contexts/loader'
import { useErrorHandler } from '@/hooks/use-error-handler'
import * as api from '@/apis/aipass'
import { RoomManagerIndicatorContext } from '@/components/pages/room-manager/indicator'
import { GuestRoomAssignResponseType } from '@/models/guest-room/guest-room-assign'
import { ApprovedStatusButton } from '@/components/molecules/approved-status-button'

type Props = {
  reservationId: string
  defaultStatus: ApprovedStatusType
  defaultRoom: string[]
  disabled?: boolean
  isDisabledEachSelection?: boolean
}

export const ChangeReservationApprovedStatus: React.FC<Props> = props => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { doReload } = useContext(RoomManagerIndicatorContext)

  const [isOpenChangeModal, setIsOpenChangeModal] = useState(false)
  const [afterState, setAfterState] = useState<ApprovedStatusType>(
    Math.min(props.defaultStatus + 1, ApprovedStatusType.Checkout) as ApprovedStatusType,
  )
  const [guestRoomForReservation, setGuestRoomForReservation] = useState<GuestRoomAssignResponseType['rooms']>([])
  const [selectAssignIds, setSelectAssignIds] = useState<string[]>(props.defaultRoom)

  const hasSalesPaid = async () => {
    const res = await api.fetchSalesReservation(props.reservationId)
    return res.sales.some(item => item.isPaid && item.isConfirm)
  }

  const onChangeStatus = async () => {
    try {
      setIsLoading(true)
      const isCheckedIn = ([ApprovedStatusType.Stay, ApprovedStatusType.Checkout] as ApprovedStatusType[]).includes(props.defaultStatus)
      if (isCheckedIn && afterState === ApprovedStatusType.Checkin && (await hasSalesPaid())) {
        alert('計上済の科目があるので、ステータスを変更できません。')
        return
      }
      await api.updateGuestRoomAssignStatus({
        reservationId: props.reservationId,
        guestRoomAssignIds: selectAssignIds,
        status: afterState,
      })
      doReload && doReload()
      setIsOpenChangeModal(false)
    } catch (e) {
      errorHandler(e)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!props.reservationId) {
      return
    }

    api.fetchGuestRoomForReservation(props.reservationId).then(res => {
      setGuestRoomForReservation(res || [])
      if (props.isDisabledEachSelection) {
        setSelectAssignIds((res || []).map(r => r.guestRoomAssignId))
      }
    })
  }, [props.reservationId])

  const StatusColor = {
    [ApprovedStatusType.Reservation]: '#F2A40B',
    [ApprovedStatusType.Checkin]: '#F47110',
    [ApprovedStatusType.Stay]: '#3E85CC',
    [ApprovedStatusType.Checkout]: '#7DC931',
  }
  const StatusText = {
    [ApprovedStatusType.Reservation]: t('Reserve'),
    [ApprovedStatusType.Checkin]: t('Checkin'),
    [ApprovedStatusType.Stay]: t('During your stay'),
    [ApprovedStatusType.Checkout]: t('Checkout'),
  }

  const isDisableSubmit = useMemo(() => selectAssignIds.length === 0, [selectAssignIds])

  return (
    <>
      <ApprovedStatusButton
        currentStatus={props.defaultStatus}
        onClick={() => {
          !props.disabled && setIsOpenChangeModal(true)
        }}
        disabled={props.disabled}
      />

      {isOpenChangeModal && (
        <Modal
          customCss={css({
            height: 450,
            width: 600,
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            margin: 0,
          })}
        >
          <ModalHeader>{t('Status change')}</ModalHeader>
          <ModalBody customCss={{ paddingBlock: 24 }}>
            <div css={modalContainerStyle}>
              <div className="status-select-block">
                <div>{t('Status after change')}</div>
                <Select
                  placeholder={t('Please select')}
                  value={afterState}
                  onChange={v => setAfterState(Number(v) as ApprovedStatusType)}
                  customStyle={css({ paddingBottom: 0 })}
                >
                  {[ApprovedStatusType.Checkin, ApprovedStatusType.Stay, ApprovedStatusType.Checkout].map((status, i) => (
                    <AntSelect.Option key={i} value={status}>
                      {StatusText[status]}
                    </AntSelect.Option>
                  ))}
                </Select>
              </div>
              <div className="room-select-block">
                <div className="-title">{t('Select target rooms')}</div>
                {guestRoomForReservation.map(room => (
                  <div key={room.guestRoomAssignId} className="-room">
                    <Checkbox
                      value={selectAssignIds.includes(room.guestRoomAssignId)}
                      label={
                        <div className="room-label">
                          <div>{room.roomNumber}</div>
                          <div>{room.assignGuestName || '-'}</div>
                          <div className="status-badge" style={{ backgroundColor: StatusColor[room.assignStatus] }}>
                            {StatusText[room.assignStatus]}
                          </div>
                        </div>
                      }
                      onChange={v => {
                        if (v) {
                          setSelectAssignIds([...selectAssignIds, room.guestRoomAssignId])
                        } else {
                          setSelectAssignIds(selectAssignIds.filter(id => id !== room.guestRoomAssignId))
                        }
                      }}
                      disabled={props.isDisabledEachSelection}
                    />
                  </div>
                ))}
                <div className="-footer">
                  <Checkbox
                    value={selectAssignIds.length === guestRoomForReservation.length}
                    label={t('Select all rooms')}
                    onChange={v => {
                      setSelectAssignIds(v ? guestRoomForReservation.map(r => r.guestRoomAssignId) : [])
                    }}
                    disabled={props.isDisabledEachSelection}
                  />
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button width={110} height={38} buttonType={ButtonType.Secondary} marginRight={16} onClick={() => setIsOpenChangeModal(false)}>
              {t('Cancel')}
            </Button>
            <Button
              width={150}
              height={40}
              disabled={isDisableSubmit}
              buttonType={isDisableSubmit ? ButtonType.PrimaryDisabled : ButtonType.Primary}
              onClick={onChangeStatus}
            >
              {t('Change status')}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  )
}
// 1px solid rgb(204, 204, 204)
// 1px solid rgb(242, 242, 242)
const modalContainerStyle = css({
  background: '#fff',
  padding: 32,
  borderRadius: 5,
  minHeight: '100%',
  cursor: 'auto',

  // text
  fontSize: 14,
  fontWeight: 'bold',
  letterSpacing: 0.7,
  color: '#272727',

  '.status-select-block': {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    whiteSpace: 'nowrap',
    '> div:last-of-type': { width: '100%' },
  },
  '.room-select-block': {
    marginTop: 16,
    padding: 16,
    border: '1px solid rgb(204, 204, 204)',
    borderRadius: 5,
    '.-room': {
      paddingTop: 8,
      '.room-label': {
        display: 'flex',
        gap: 8,
        alignItems: 'center',
      },
      '.status-badge': {
        color: 'white',
        padding: '0 16px',
        borderRadius: 50,
        lineHeight: '24px',
      },
    },
    '.-footer': {
      marginTop: 8,
      paddingTop: 8,
      borderTop: '1px solid rgb(204, 204, 204)',
    },
  },
})
